import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';
import { useSendActionDevice } from '../../../../queries/devices';
import { getScreenshotBaseline, pollForNewScreenshot } from '../../../../utils/screenshot';

/**
 * Smart Template Engine — Preview System (Phase 3B): Wall Screenshot Composer.
 *
 * Triggers a screenshot on every device in the wall (in parallel), polls Firebase
 * Storage `screenshots/{serial}.png` for each, and composites them into one
 * grid-shaped image. Partial-failure tolerant (a screen that times out falls back
 * to its last screenshot, else a placeholder). Download via <canvas> (best-effort;
 * needs Storage CORS — the on-screen composite always works).
 *
 * Reuses the existing per-device screenshot pipeline. Read-only — no sync contact.
 */
export default function WallScreenshotComposer({ wall, maxWidth = 640 }) {
  const { t } = useTranslation();
  const sendAction = useSendActionDevice();
  const [shots, setShots] = useState({}); // { [pos]: { status, url } }
  const [capturing, setCapturing] = useState(false);
  const [stamp, setStamp] = useState(null);

  const rows = Math.max(1, wall?.rows || 1);
  const cols = Math.max(1, wall?.columns || 1);
  const portrait = wall?.orientation === 'portrait';
  const cellAspect = portrait ? 9 / 16 : 16 / 9;
  const bezel = 4;
  const cellW = (maxWidth - (cols - 1) * bezel) / cols;
  const cellH = cellW / cellAspect;

  const screensByPos = useMemo(() => {
    const m = {};
    (wall?.screens || []).forEach((s) => { m[s.grid_position] = s; });
    return m;
  }, [wall]);

  const setShot = (pos, v) => setShots((p) => ({ ...p, [pos]: v }));

  const capture = async () => {
    setCapturing(true);
    setShots({});
    const positions = Array.from({ length: rows * cols }, (_, i) => i + 1);
    await Promise.all(positions.map(async (pos) => {
      const s = screensByPos[pos];
      const serial = s?.device?.serial_name;
      const devId = s?.device?.id;
      if (!serial || !devId) { setShot(pos, { status: 'empty' }); return; }
      setShot(pos, { status: 'capturing' });
      try {
        const baseline = await getScreenshotBaseline(serial);
        await sendAction.mutateAsync({ id: devId, data: { action: 'screenshot' } });
        // FRESH ONLY — only show an image captured THIS session (updated > baseline).
        // Never fall back to an old Storage image (that showed days-old error/campaign
        // screens as if live — a trust bug). A device that doesn't produce a fresh
        // capture is shown as "couldn't capture", not a stale picture.
        const res = await pollForNewScreenshot(serial, baseline);
        setShot(pos, res?.url ? { status: 'ready', url: res.url } : { status: 'failed' });
      } catch (e) {
        setShot(pos, { status: 'failed' });
      }
    }));
    setStamp(new Date());
    setCapturing(false);
  };

  const download = async () => {
    const exW = 480; const exH = Math.round(exW / cellAspect);
    const canvas = document.createElement('canvas');
    canvas.width = cols * exW; canvas.height = rows * exH;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    const positions = Array.from({ length: rows * cols }, (_, i) => i + 1);
    await Promise.all(positions.map((pos) => new Promise((resolve) => {
      const url = shots[pos]?.url;
      if (!url) { resolve(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const r = Math.floor((pos - 1) / cols); const c = (pos - 1) % cols;
        try { ctx.drawImage(img, c * exW, r * exH, exW, exH); } catch (e) { /* ignore */ }
        resolve();
      };
      img.onerror = () => resolve();
      img.src = url;
    })));
    try {
      const link = document.createElement('a');
      link.download = `wall-${wall?.id || 'composite'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      // Tainted canvas (Storage CORS not allowing this origin) — the on-screen
      // composite is still available; download just isn't possible here.
      // eslint-disable-next-line no-alert
      alert(t('video_wall.shot.download_blocked'));
    }
  };

  const anyReady = Object.values(shots).some((s) => s?.url);

  return (
    <div className="d-flex flex-column align-items-center gap-3">
      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center">
        <Button label={t('video_wall.shot.capture')} icon="pi pi-camera" onClick={capture} loading={capturing} />
        <Button label={t('video_wall.shot.download')} icon="pi pi-download" outlined onClick={download}
          disabled={!anyReady || capturing} />
        {stamp && <span className="small text-muted">{stamp.toLocaleString()}</span>}
      </div>

      <div dir="ltr" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, ${cellW}px)`,
        gridTemplateRows: `repeat(${rows}, ${cellH}px)`, gap: bezel, background: '#000', padding: bezel,
        borderRadius: 10, width: 'fit-content' }}>
        {Array.from({ length: rows * cols }, (_, i) => i + 1).map((pos) => {
          const sh = shots[pos];
          return (
            <div key={pos} style={{ width: cellW, height: cellH, overflow: 'hidden', borderRadius: 4,
              background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)', position: 'relative' }}>
              {sh?.url ? (
                <img src={sh.url} alt={`#${pos}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : sh?.status === 'capturing' ? (
                <i className="pi pi-spin pi-spinner" />
              ) : sh?.status === 'empty' ? (
                <span style={{ fontSize: 11 }}>{t('video_wall.unassigned')}</span>
              ) : sh?.status === 'failed' ? (
                <span style={{ fontSize: 11, lineHeight: 1.3 }}>
                  <i className="pi pi-times-circle d-block mb-1" style={{ color: 'var(--pp-danger)' }} />
                  {t('video_wall.shot.failed')}
                </span>
              ) : (
                <span style={{ fontSize: 12 }}>#{pos}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
